import { AutofillMatch, AutofillResult } from "./types";

import {
  type SourceItem,
  SourceType,
  type SourcesV2,
} from "@/components/PowerBlock/Block/SourcesField/sourceTypes";
import { calculateStringSimilarity, normalizeForFuzzySearch } from "@/utils/fuzzy-search";

/**
 * Normalizes a string for comparison by converting to lowercase and removing special characters
 */
const normalizeString = normalizeForFuzzySearch;

/**
 * Converts snake_case to camelCase
 */
const snakeToCamel = (str: string): string =>
  str.toLowerCase().replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

/**
 * Converts camelCase to snake_case
 */
const camelToSnake = (str: string): string =>
  str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

/**
 * Finds the best match for a variable name among available sources
 */
interface AutofillSourceCandidate {
  label: string;
  value: string;
}

const itemToCandidate = (item: SourceItem): AutofillSourceCandidate => ({
  label: item.label,
  value: item.value,
});

const getAutofillCandidates = (
  sources: Record<SourceType, string[]>,
  sourcesV2?: SourcesV2 | null,
): AutofillSourceCandidate[] => {
  if (sourcesV2) {
    const candidates: AutofillSourceCandidate[] = [];
    const seenValues = new Set<string>();

    const pushItems = (items: SourceItem[]) => {
      for (const item of items) {
        if (seenValues.has(item.value)) {
          continue;
        }
        seenValues.add(item.value);
        candidates.push(itemToCandidate(item));
      }
    };

    for (const sourceType of Object.values(SourceType)) {
      pushItems(sourcesV2[sourceType] ?? []);
    }

    for (const group of sourcesV2.columnGroups ?? []) {
      pushItems(group.items);
    }

    for (const sheet of sourcesV2.sheets ?? []) {
      pushItems(sheet.items);
    }

    return candidates;
  }

  return Object.values(sources)
    .flat()
    .map(source => ({ label: source, value: source }));
};

export const findBestMatch = (
  variableName: string,
  availableSources: string[],
): AutofillMatch | null => {
  if (!variableName || availableSources.length === 0) {
    return null;
  }

  const normalizedVariable = normalizeString(variableName);
  const variableSnake = camelToSnake(variableName);
  const variableCamel = snakeToCamel(variableName);

  let bestMatch: AutofillMatch | null = null;
  let bestScore = 0;

  for (const source of availableSources) {
    // First priority: Exact match (case-sensitive)
    if (variableName === source) {
      return {
        variableName,
        sourceKey: source,
        matchType: "exact",
        confidence: 1.0,
      };
    }
  }

  for (const source of availableSources) {
    const normalizedSource = normalizeString(source);
    const sourceSnake = camelToSnake(source);
    const sourceCamel = snakeToCamel(source);

    // Second priority: Check for exact match with dot-notation segments
    // e.g., "Ticket" should exactly match the "Ticket" part of "variable.Ticket"
    const sourceSegments = source.split(".");
    const lastSegment = sourceSegments[sourceSegments.length - 1];

    // Exact match with last segment
    if (variableName === lastSegment) {
      const match: AutofillMatch = {
        variableName,
        sourceKey: source,
        matchType: "exact",
        confidence: 1,
      };
      bestMatch = match;
      bestScore = 1;
      break;
    }

    // Case-insensitive exact match with last segment
    if (variableName.toLowerCase() === lastSegment.toLowerCase()) {
      const match: AutofillMatch = {
        variableName,
        sourceKey: source,
        matchType: "exact",
        confidence: 0.93,
      };
      if (bestScore < 0.93) {
        bestMatch = match;
        bestScore = 0.93;
      }
      continue;
    }

    // Case format conversions for full string
    if (
      variableSnake === source ||
      variableCamel === source ||
      variableName === sourceSnake ||
      variableName === sourceCamel
    ) {
      const match: AutofillMatch = {
        variableName,
        sourceKey: source,
        matchType: "exact",
        confidence: 0.91,
      };
      if (bestScore < 0.91) {
        bestMatch = match;
        bestScore = 0.91;
      }
      continue;
    }

    // Check segment matches with case conversions
    const lastSegmentSnake = camelToSnake(lastSegment);
    const lastSegmentCamel = snakeToCamel(lastSegment);
    if (
      variableSnake === lastSegment ||
      variableCamel === lastSegment ||
      variableName === lastSegmentSnake ||
      variableName === lastSegmentCamel
    ) {
      const match: AutofillMatch = {
        variableName,
        sourceKey: source,
        matchType: "exact",
        confidence: 0.89,
      };
      if (bestScore < 0.89) {
        bestMatch = match;
        bestScore = 0.89;
      }
      continue;
    }

    // Normalized exact match (catches remaining edge cases)
    if (normalizedVariable === normalizedSource) {
      const match: AutofillMatch = {
        variableName,
        sourceKey: source,
        matchType: "exact",
        confidence: 0.87,
      };
      if (bestScore < 0.87) {
        bestMatch = match;
        bestScore = 0.87;
      }
      continue;
    }

    // Partial matches
    if (
      normalizedSource.includes(normalizedVariable) ||
      normalizedVariable.includes(normalizedSource)
    ) {
      const confidence =
        Math.max(
          normalizedVariable.length / normalizedSource.length,
          normalizedSource.length / normalizedVariable.length,
        ) * 0.8;

      if (confidence > bestScore && confidence > 0.6) {
        bestMatch = {
          variableName,
          sourceKey: source,
          matchType: "partial",
          confidence,
        };
        bestScore = confidence;
      }
      continue;
    }

    // Fuzzy matching
    const similarity = calculateStringSimilarity(normalizedVariable, normalizedSource);
    if (similarity > 0.7 && similarity > bestScore) {
      bestMatch = {
        variableName,
        sourceKey: source,
        matchType: "fuzzy",
        confidence: similarity * 0.7, // Reduce confidence for fuzzy matches
      };
      bestScore = similarity * 0.7;
    }
  }

  return bestMatch;
};

const findBestCandidateMatch = (
  variableName: string,
  candidates: AutofillSourceCandidate[],
): AutofillMatch | null => {
  const match = findBestMatch(
    variableName,
    candidates.map(candidate => candidate.label),
  );

  if (!match) {
    return null;
  }

  const candidate = candidates.find(({ label }) => label === match.sourceKey);
  if (!candidate) {
    return match;
  }

  return {
    ...match,
    sourceKey: candidate.value,
  };
};

/**
 * Generates automatic mappings for unmapped variables (no filtering needed).
 * Only maps when there is a high-confidence name match against available sources
 * (including Trace/Request columnGroups).
 */
export const generateAutoMappings = (
  unmappedVariables: string[],
  sources: Record<SourceType, string[]>,
  sourcesV2?: SourcesV2 | null,
): AutofillResult => {
  const candidates = getAutofillCandidates(sources, sourcesV2);
  const matches: AutofillMatch[] = [];
  const mappings: Record<string, string> = {};
  const usedSourceValues = new Set<string>();

  for (const variable of unmappedVariables) {
    const availableCandidates = candidates.filter(
      candidate => !usedSourceValues.has(candidate.value),
    );
    const match = findBestCandidateMatch(variable, availableCandidates);
    if (match && match.confidence >= 0.6) {
      matches.push(match);
      mappings[variable] = match.sourceKey;
      usedSourceValues.add(match.sourceKey);
    }
  }

  return {
    mappings,
    matches,
    totalPossible: unmappedVariables.length,
    totalMapped: matches.length,
  };
};

/**
 * Gets all available source keys as a flat array
 */
export const getAllSourceKeys = (
  sources: Record<SourceType, string[]>,
  sourcesV2?: SourcesV2 | null,
): string[] => getAutofillCandidates(sources, sourcesV2).map(candidate => candidate.value);

/**
 * Checks if unmapped variables have autofill suggestions (no filtering needed)
 */
export const hasAutofillSuggestions = (
  unmappedVariables: string[],
  sources: Record<SourceType, string[]>,
  sourcesV2?: SourcesV2 | null,
): boolean => {
  if (unmappedVariables.length === 0) {
    return false;
  }

  return generateAutoMappings(unmappedVariables, sources, sourcesV2).totalMapped > 0;
};

/**
 * Gets count of unmapped variables that can be auto-mapped (no filtering needed)
 */
export const getAutofillableCount = (
  unmappedVariables: string[],
  sources: Record<SourceType, string[]>,
  sourcesV2?: SourcesV2 | null,
): number => {
  if (unmappedVariables.length === 0) {
    return 0;
  }

  return generateAutoMappings(unmappedVariables, sources, sourcesV2).totalMapped;
};
